import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Chip,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Alert,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Schedule as ScheduleIcon,
  Security as SecurityIcon,
  Edit as EditIcon,
  PersonOff as DeactivateIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useUsers, useBulkUpdateUsers, useUpdateUserStatus } from '../../hooks/useUsers';
import DevOnly from '../../components/DevOnly';

const UsersList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Prepare query parameters
  const queryParams = {
    search: searchTerm,
    status: statusFilter,
    page: page + 1, // API uses 1-based pagination
    limit: pageSize
  };

  // Fetch users data
  const { data, isLoading, error, refetch } = useUsers(queryParams);
  const { mutate: bulkUpdateUsers } = useBulkUpdateUsers();
  const { mutate: updateUserStatus } = useUpdateUserStatus();

  const handleMenuClick = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleUserClick = (userId) => {
    navigate(`/users/${userId}`);
  };

  const handleBulkAction = (action) => {
    switch (action) {
      case 'activate':
        bulkUpdateUsers({ 
          userIds: selectedUsers, 
          updates: { status: 'active' } 
        });
        break;
      case 'deactivate':
        bulkUpdateUsers({ 
          userIds: selectedUsers, 
          updates: { status: 'inactive' } 
        });
        break;
      default:
        break;
    }
    setSelectedUsers([]);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedUsers(users?.map(user => user.id) || []);
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Use the data from API or fall back to empty array
  const users = data?.users || [];
  const totalCount = data?.total || 0;

  // Calculate stats based on actual status values
  // Note: We only count users with explicit status values from Pabau
  const activeUsersCount = users.filter(u => u.status && u.status.toLowerCase() === 'active').length;
  const pendingUsersCount = users.filter(u => u.status && u.status.toLowerCase() === 'pending').length;
  const withAppointmentsCount = users.filter(u => u.total_appointments > 0).length;

  const columns = [
    {
      field: 'checkbox',
      headerName: '',
      width: 50,
      sortable: false,
      renderHeader: () => (
        <Checkbox
          checked={selectedUsers.length === users.length && users.length > 0}
          indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
          onChange={handleSelectAll}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={selectedUsers.includes(params.row.id)}
          onChange={() => handleSelectUser(params.row.id)}
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    {
      field: 'user',
      headerName: 'User',
      width: 320,
      renderCell: (params) => (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          cursor: 'pointer',
          py: 1,
          minHeight: 60
        }}>
          <Avatar 
            sx={{ 
              mr: 2, 
              bgcolor: 'primary.main',
              width: 40,
              height: 40
            }}
            src={params.row.avatar}
          >
            {params.row.full_name?.split(' ').map(n => n[0]).join('') || 
             params.row.username?.[0]?.toUpperCase() || '?'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="body1" 
              fontWeight="600"
              sx={{ 
                lineHeight: 1.2,
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {params.row.full_name || params.row.username || 'Unknown User'}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                lineHeight: 1.1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {params.row.username || params.row.job_title || ''}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      field: 'job_title',
      headerName: 'Job Title',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.job_title || 'No title'}
        </Typography>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 220,
      renderCell: (params) => {
        // Pabau stores email in the username field
        const email = params.row.username || params.row.email;
        return (
          <Typography variant="body2" color={email ? 'text.primary' : 'text.secondary'}>
            {email || 'No email'}
          </Typography>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const status = params.row.status;
        if (!status || status === '') {
          return (
            <Typography variant="body2" color="text.secondary">
              No status
            </Typography>
          );
        }
        // Handle different status values from Pabau
        // Note: Pabau API often returns empty strings for status
        // Empty status = user is active (synced from Pabau)
        if (!status || !status.trim()) {
          return (
            <Chip
              icon={<ActiveIcon fontSize="small" />}
              label="Active"
              color="success"
              size="small"
              variant="outlined"
            />
          );
        }

        const statusLower = status.toLowerCase();
        let color = 'default';
        let icon = null;

        if (statusLower === 'active') {
          color = 'success';
          icon = <ActiveIcon fontSize="small" />;
        } else if (statusLower === 'pending') {
          color = 'warning';
          icon = <InactiveIcon fontSize="small" />;
        } else if (statusLower === 'inactive') {
          color = 'error';
          icon = <InactiveIcon fontSize="small" />;
        }

        return (
          <Chip
            icon={icon}
            label={status}
            color={color}
            size="small"
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Profile">
            <IconButton
              size="small"
              onClick={() => handleUserClick(params.row.id)}
              sx={{ mr: 1 }}
            >
              <PersonAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <DevOnly>
            <IconButton
              size="small"
              onClick={(e) => handleMenuClick(e, params.row)}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </DevOnly>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Users & Staff
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage clinic staff, roles, and permissions
          </Typography>
        </Box>
        <DevOnly>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/users/new')}
            sx={{ height: 'fit-content' }}
          >
            Add User
          </Button>
        </DevOnly>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="primary.main">
                {totalCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="success.main">
                {activeUsersCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="warning.main">
                {pendingUsersCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Users
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="secondary.main">
                {withAppointmentsCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                With Appointments
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
              size="medium"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="medium" sx={{ minWidth: 220 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
              }}
              size="large"
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Actions Bar */}
      {selectedUsers.length > 0 && (
        <DevOnly>
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography>
                {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
              </Typography>
              <Button 
                variant="contained" 
                color="success" 
                size="small"
                onClick={() => handleBulkAction('activate')}
              >
                Activate
              </Button>
              <Button 
                variant="contained" 
                color="error" 
                size="small"
                onClick={() => handleBulkAction('deactivate')}
              >
                Deactivate
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => setSelectedUsers([])}
                sx={{ 
                  color: 'primary.contrastText', 
                  borderColor: 'primary.contrastText' 
                }}
              >
                Clear Selection
              </Button>
            </Box>
          </Paper>
        </DevOnly>
      )}

      {/* Loading and Error States */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load users: {error.message}
          <Button onClick={() => refetch()} size="small" sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Users DataGrid */}
      <Paper sx={{ height: 'auto', minHeight: 600, width: '100%' }}>
        <DataGrid
          rows={users}
          columns={columns}
          loading={isLoading}
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          rowCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[25, 50, 100]}
          disableSelectionOnClick
          onRowClick={(params) => handleUserClick(params.id)}
          checkboxSelection={false}
          rowHeight={70}
          autoHeight
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'grey.50',
              borderBottom: '1px solid',
              borderBottomColor: 'grey.300',
              fontSize: '0.875rem',
              fontWeight: 600
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover'
              },
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                '&:hover': {
                  backgroundColor: 'primary.light'
                }
              }
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid',
              borderBottomColor: 'grey.200',
              display: 'flex',
              alignItems: 'center'
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              minHeight: '400px !important'
            }
          }}
        />
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          navigate(`/users/${selectedUser?.id}`);
          handleMenuClose();
        }}>
          <PersonAddIcon sx={{ mr: 1 }} fontSize="small" />
          View Profile
        </MenuItem>
        <DevOnly>
          <MenuItem onClick={() => {
            navigate(`/users/${selectedUser?.id}/edit`);
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit User
          </MenuItem>
        </DevOnly>
        <DevOnly>
          <MenuItem onClick={() => {
            navigate(`/users/${selectedUser?.id}#schedule`);
            handleMenuClose();
          }}>
            <ScheduleIcon sx={{ mr: 1 }} fontSize="small" />
            Manage Schedule
          </MenuItem>
        </DevOnly>
        <DevOnly>
          <MenuItem onClick={() => {
            navigate(`/users/${selectedUser?.id}#permissions`);
            handleMenuClose();
          }}>
            <SecurityIcon sx={{ mr: 1 }} fontSize="small" />
            Edit Permissions
          </MenuItem>
        </DevOnly>
        <DevOnly>
          <MenuItem 
            onClick={() => {
              updateUserStatus({ 
                userId: selectedUser?.id, 
                status: selectedUser?.status === 'active' ? 'inactive' : 'active'
              });
              handleMenuClose();
            }}
            sx={{ color: selectedUser?.status === 'active' ? 'error.main' : 'success.main' }}
          >
            <DeactivateIcon sx={{ mr: 1 }} fontSize="small" />
            {selectedUser?.status === 'active' ? 'Deactivate' : 'Activate'}
          </MenuItem>
        </DevOnly>
      </Menu>
    </Box>
  );
};

export default UsersList;